/** Couleur 0xRRGGBB. */
export type Color = number;

function channels(color: Color): [number, number, number] {
  return [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff];
}

function pack(r: number, g: number, b: number): Color {
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}

/** Mélange de deux couleurs (`t` de 0 à 1). */
export function mix(a: Color, b: Color, t: number): Color {
  const [ar, ag, ab] = channels(a);
  const [br, bg, bb] = channels(b);
  return pack(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Éclaircit (`amount` > 0, vers le blanc) ou assombrit (< 0, vers un bleu nuit) une couleur. */
export function shade(color: Color, amount: number): Color {
  return amount >= 0 ? mix(color, 0xffffff, amount) : mix(color, 0x05070f, -amount);
}

export interface BoxFaces {
  top: Color;
  left: Color;
  right: Color;
  /** Arêtes avant du dessus, prises par la lumière. */
  rim?: Color;
}

export interface CylinderColors {
  top: Color;
  light: Color;
  side: Color;
  dark: Color;
}

/**
 * Toile de pixels d'art : un tampon RGBA où l'on ne pose que des pixels entiers, sans lissage. Les sprites
 * du jeu y sont dessinés en code, puis confiés au moteur de rendu.
 */
export class PixelCanvas {
  readonly data: Uint8ClampedArray;

  constructor(
    readonly width: number,
    readonly height: number,
  ) {
    this.data = new Uint8ClampedArray(width * height * 4);
  }

  inside(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  set(x: number, y: number, color: Color, alpha = 255): void {
    const px = Math.floor(x);
    const py = Math.floor(y);
    if (!this.inside(px, py)) return;
    const i = (py * this.width + px) * 4;
    this.data[i] = (color >> 16) & 0xff;
    this.data[i + 1] = (color >> 8) & 0xff;
    this.data[i + 2] = color & 0xff;
    this.data[i + 3] = alpha;
  }

  alphaAt(x: number, y: number): number {
    if (!this.inside(x, y)) return 0;
    return this.data[(y * this.width + x) * 4 + 3] ?? 0;
  }

  colorAt(x: number, y: number): Color {
    const i = (y * this.width + x) * 4;
    return pack(this.data[i] ?? 0, this.data[i + 1] ?? 0, this.data[i + 2] ?? 0);
  }

  rect(x: number, y: number, w: number, h: number, color: Color, alpha = 255): void {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.set(x + i, y + j, color, alpha);
  }

  hline(x0: number, x1: number, y: number, color: Color): void {
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) this.set(x, y, color);
  }

  vline(x: number, y0: number, y1: number, color: Color): void {
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) this.set(x, y, color);
  }

  /** Segment de Bresenham. */
  line(x0: number, y0: number, x1: number, y1: number, color: Color): void {
    let x = x0;
    let y = y0;
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let error = dx + dy;
    for (;;) {
      this.set(x, y, color);
      if (x === x1 && y === y1) return;
      const e2 = 2 * error;
      if (e2 >= dy) {
        error += dy;
        x += sx;
      }
      if (e2 <= dx) {
        error += dx;
        y += sy;
      }
    }
  }

  /**
   * Demi-largeur, à la ligne `row`, d'un losange de `w` × `h` pixels (rapport quelconque : 2:1, 3:2…). Les
   * deux lignes du milieu ont toute la largeur ; les pointes font deux ou trois pixels.
   */
  static diamondHalf(w: number, h: number, row: number): number {
    const mid = h / 2;
    const step = row < mid ? row + 1 : h - row;
    return Math.max(1, Math.round((step / mid) * (w / 2)));
  }

  /** Dernière ligne du losange `w` × `h` qui couvre la colonne `i` (pour poser les faces d'un bloc). */
  static diamondBottomRow(w: number, h: number, i: number): number {
    const dx = i < w / 2 ? w / 2 - i : i - w / 2 + 1;
    for (let row = h - 1; row >= h / 2; row--) {
      if (PixelCanvas.diamondHalf(w, h, row) >= dx) return row;
    }
    return Math.floor(h / 2);
  }

  /** Losange isométrique de `w` × `h`, coin haut-gauche de son cadre en (left, top). */
  diamond(left: number, top: number, w: number, h: number, fill: Color, edge?: Color, alpha = 255): void {
    const cx = left + w / 2;
    for (let row = 0; row < h; row++) {
      const half = PixelCanvas.diamondHalf(w, h, row);
      for (let x = cx - half; x < cx + half; x++) {
        const onEdge = x < cx - half + 2 || x >= cx + half - 2;
        this.set(x, top + row, onEdge && edge !== undefined ? edge : fill, alpha);
      }
    }
  }

  /** Arêtes seules d'un losange (deux pixels par ligne de chaque côté). */
  diamondEdge(left: number, top: number, w: number, h: number, color: Color): void {
    const cx = left + w / 2;
    for (let row = 0; row < h; row++) {
      const half = PixelCanvas.diamondHalf(w, h, row);
      for (const x of [cx - half, cx - half + 1, cx + half - 2, cx + half - 1]) this.set(x, top + row, color);
    }
  }

  /** Points des quatre coins d'un losange (arêtes d'échafaudage, marques). */
  static diamondCorners(
    left: number,
    top: number,
    w: number,
    h: number,
  ): { top: [number, number]; right: [number, number]; bottom: [number, number]; left: [number, number] } {
    const cx = left + w / 2;
    const cy = top + Math.floor(h / 2);
    return { top: [cx, top], right: [left + w - 1, cy], bottom: [cx, top + h - 1], left: [left, cy] };
  }

  /**
   * Bloc isométrique : losange du dessus et deux faces visibles, posé sur le losange de sol (`w` × `h`) dont
   * le cadre commence en (left, groundTop). Renvoie la ligne du haut du toit.
   */
  isoBox(left: number, groundTop: number, w: number, h: number, height: number, faces: BoxFaces): number {
    const roofTop = groundTop - height;
    for (let i = 0; i < w; i++) {
      const bottomRow = PixelCanvas.diamondBottomRow(w, h, i);
      const color = i < w / 2 ? faces.left : faces.right;
      for (let y = roofTop + bottomRow + 1; y <= roofTop + bottomRow + height; y++) this.set(left + i, y, color);
    }
    this.diamond(left, roofTop, w, h, faces.top);
    if (faces.rim !== undefined) {
      // Arête avant gauche du toit : la lumière accroche le bord.
      const cx = left + w / 2;
      for (let row = Math.floor(h / 2); row < h; row++) {
        const half = PixelCanvas.diamondHalf(w, h, row);
        this.set(cx - half, roofTop + row, faces.rim);
        this.set(cx - half + 1, roofTop + row, faces.rim);
      }
    }
    return roofTop;
  }

  /** Ellipse pleine. */
  ellipse(cx: number, cy: number, rx: number, ry: number, color: Color): void {
    for (let dy = -ry; dy <= ry; dy++) {
      const half = Math.round(rx * Math.sqrt(Math.max(0, 1 - (dy / ry) ** 2)));
      for (let dx = -half; dx < half; dx++) this.set(cx + dx, cy + dy, color);
    }
  }

  /**
   * Cylindre vertical (réservoir, tour) : base au centre (cx, baseY), rayon `rx`, hauteur `height`. `squash`
   * aplatit les ellipses comme les losanges de la vue (hauteur sur largeur).
   */
  cylinder(cx: number, baseY: number, rx: number, height: number, colors: CylinderColors, squash = 0.5): void {
    const ry = Math.max(1, Math.round(rx * squash));
    for (let dx = -rx; dx < rx; dx++) {
      const half = Math.round(ry * Math.sqrt(Math.max(0, 1 - ((dx + 0.5) / rx) ** 2)));
      const t = (dx + rx) / (2 * rx);
      const color = t < 0.25 ? colors.light : t < 0.7 ? colors.side : colors.dark;
      for (let y = baseY - height; y <= baseY + half; y++) this.set(cx + dx, y, color);
    }
    this.ellipse(cx, baseY - height, rx, ry, colors.top);
  }

  /** Dôme : demi-ellipse posée sur sa base, avec un reflet. */
  dome(cx: number, baseY: number, rx: number, ry: number, color: Color, light: Color, dark: Color): void {
    for (let dy = -ry; dy <= 0; dy++) {
      const half = Math.round(rx * Math.sqrt(Math.max(0, 1 - (dy / ry) ** 2)));
      for (let dx = -half; dx < half; dx++) {
        const t = (dx + half) / Math.max(1, 2 * half);
        this.set(cx + dx, baseY + dy, t > 0.72 ? dark : color);
      }
    }
    // Reflet en haut à gauche.
    this.set(cx - Math.round(rx / 2), baseY - Math.round(ry * 0.6), light);
    this.set(cx - Math.round(rx / 2) + 1, baseY - Math.round(ry * 0.6), light);
    this.set(cx - Math.round(rx / 2), baseY - Math.round(ry * 0.6) + 1, light);
  }

  /** Trame en damier sur les pixels déjà posés d'une zone (ombre, verre, nuit). */
  dither(x: number, y: number, w: number, h: number, color: Color): void {
    for (let j = 0; j < h; j++) {
      for (let i = 0; i < w; i++) {
        if ((x + i + y + j) % 2 === 0 && this.alphaAt(x + i, y + j) > 0) this.set(x + i, y + j, color);
      }
    }
  }

  /**
   * Contour d'une pièce : chaque pixel vide qui touche un pixel plein (silhouette lisible, façon arcade). Les
   * pixels à demi transparents (ombre portée) ne comptent pas comme pleins.
   */
  outline(color: Color): void {
    const solid = (x: number, y: number) => this.alphaAt(x, y) >= 128;
    const marks: [number, number][] = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (solid(x, y)) continue;
        if (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1)) marks.push([x, y]);
      }
    }
    for (const [x, y] of marks) this.set(x, y, color);
  }

  /** Copie une autre toile (pixels pleins seulement) à la position donnée. */
  stamp(source: PixelCanvas, x: number, y: number): void {
    for (let j = 0; j < source.height; j++) {
      for (let i = 0; i < source.width; i++) {
        const alpha = source.alphaAt(i, j);
        if (alpha > 0) this.set(x + i, y + j, source.colorAt(i, j), alpha);
      }
    }
  }

  /** Canevas du navigateur, prêt à devenir une texture. */
  toCanvas(): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = this.width;
    canvas.height = this.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canevas 2D indisponible");
    context.putImageData(new ImageData(new Uint8ClampedArray(this.data), this.width, this.height), 0, 0);
    return canvas;
  }
}
